/**
 * Maintains a small, browser-local recently viewed product history.
 *
 * No customer or account data is stored. Each entry contains only public
 * catalog identifiers and the time at which the product was viewed.
 */
export class RecentlyViewed {
  static STORAGE_KEY = 'tileclub:recently-viewed:v1';
  static LEGACY_STORAGE_KEY = 'viewedProducts';
  static MAX_PRODUCTS = 12;

  /**
   * Add or move a product to the front of the history.
   * Storage failures are intentionally ignored so this enhancement never
   * interferes with the storefront.
   *
   * @param {{
   *   id: string | number,
   *   handle?: string,
   *   collectionHandles?: string[],
   *   viewedAt?: number
   * } | string | number} product
   */
  static addProduct(product) {
    const entry = this.#normaliseEntry(product);
    if (!entry) return;

    entry.viewedAt = Date.now();

    const viewedProducts = [
      entry,
      ...this.getProducts().filter((viewedProduct) => viewedProduct.id !== entry.id),
    ].slice(0, this.MAX_PRODUCTS);

    this.#write(viewedProducts);
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.LEGACY_STORAGE_KEY);
    } catch {
      // localStorage can be unavailable in private or restricted contexts.
    }
  }

  /**
   * Return valid entries in most-recent-first order.
   *
   * @returns {{
   *   id: string,
   *   handle: string,
   *   viewedAt: number,
   *   collectionHandles: string[]
   * }[]}
   */
  static getProducts() {
    try {
      const storedValue = localStorage.getItem(this.STORAGE_KEY);
      const legacyValue = storedValue === null ? localStorage.getItem(this.LEGACY_STORAGE_KEY) : null;
      const parsedValue = JSON.parse(storedValue ?? legacyValue ?? '[]');

      if (!Array.isArray(parsedValue)) {
        this.#write([]);
        return [];
      }

      const viewedProducts = parsedValue
        .map((entry, index) => this.#normaliseEntry(entry, index))
        .filter(Boolean)
        .slice(0, this.MAX_PRODUCTS);

      if (legacyValue !== null && this.#write(viewedProducts)) {
        try {
          localStorage.removeItem(this.LEGACY_STORAGE_KEY);
        } catch {
          // Keep returning the valid in-memory history if removal is blocked.
        }
      }

      return viewedProducts;
    } catch {
      return [];
    }
  }

  /**
   * @param {unknown} value
   * @param {number} [legacyIndex]
   */
  static #normaliseEntry(value, legacyIndex = 0) {
    if (typeof value === 'string' || typeof value === 'number') {
      const id = String(value).trim();
      if (!/^\d+$/.test(id)) return null;

      return {
        id,
        handle: '',
        viewedAt: Date.now() - legacyIndex,
        collectionHandles: [],
      };
    }

    if (!value || typeof value !== 'object') return null;

    const entry = /** @type {Record<string, unknown>} */ (value);
    const id = String(entry.id ?? '').trim();
    if (!/^\d+$/.test(id)) return null;

    const collectionHandles = Array.isArray(entry.collectionHandles)
      ? [...new Set(entry.collectionHandles.filter((handle) => typeof handle === 'string' && handle.length > 0))]
      : [];

    return {
      id,
      handle: typeof entry.handle === 'string' ? entry.handle : '',
      viewedAt: Number.isFinite(entry.viewedAt) ? Number(entry.viewedAt) : Date.now() - legacyIndex,
      collectionHandles,
    };
  }

  /**
   * @param {ReturnType<typeof RecentlyViewed.getProducts>} viewedProducts
   */
  static #write(viewedProducts) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(viewedProducts));
      return true;
    } catch {
      // Fail silently when storage is blocked or full.
      return false;
    }
  }
}
